import type { CdnProvidersListResponse } from "@/types";

import { atom } from "jotai";
import { atomWithQuery } from "jotai-tanstack-query";

import { apiClient } from "@/utils/api";
import { buildApiParams } from "@/utils/helpers";
import { createPaginationParams, createTableQueryOptions } from "@/components/table";

// API Base Path
const CDN_PROVIDERS_BASE = '/cdn-providers';

// Shared params atom for CDN providers list
export type CdnProvidersListParams = {
  page: number;
  per_page: number;
  filters: Record<string, string | undefined>;
  refresh?: number;
};

export const cdnProvidersListParamsAtom = atom<CdnProvidersListParams>({
  ...createPaginationParams(),
  filters: {},
});

// Hydration flag to delay initial query until URL/filters are synced
export const cdnProvidersHydratedAtom = atom<boolean>(false);

// GET /cdn-providers list query (with pagination)
export const cdnProvidersQueryAtom = atomWithQuery<CdnProvidersListResponse>((get) => {
  const params = get(cdnProvidersListParamsAtom);
  const hydrated = get(cdnProvidersHydratedAtom);

  return createTableQueryOptions({
    queryKey: ["cdn-providers", params] as const,
    queryFn: async () => {
      const apiParams = buildApiParams(
        { page: params.page, per_page: params.per_page },
        params.filters || {}
      );

      const { data } = await apiClient.get(CDN_PROVIDERS_BASE, { params: apiParams });

      return data as CdnProvidersListResponse;
    },
    enabled: hydrated
  });
});

// CRUD Operations
export interface CdnProviderForm {
  name: string;
  base_url: string;
}

// Create CDN Provider
export const createCdnProvider = atom(
  null,
  async (get, set, formData: CdnProviderForm) => {
    const payload = {
      name: formData.name,
      base_url: formData.base_url
    };

    await apiClient.post(CDN_PROVIDERS_BASE, payload);

    // Trigger refresh by updating params
    const currentParams = get(cdnProvidersListParamsAtom);
    set(cdnProvidersListParamsAtom, { ...currentParams, refresh: Date.now() });
  }
);

// Update CDN Provider
export const updateCdnProvider = atom(
  null,
  async (get, set, { id, formData }: { id: number; formData: CdnProviderForm }) => {
    const payload = {
      name: formData.name,
      base_url: formData.base_url
    };

    await apiClient.put(`${CDN_PROVIDERS_BASE}/${id}`, payload);

    // Trigger refresh by updating params
    const currentParams = get(cdnProvidersListParamsAtom);
    set(cdnProvidersListParamsAtom, { ...currentParams, refresh: Date.now() });
  }
);

// Delete CDN Provider
export const deleteCdnProvider = atom(
  null,
  async (get, set, id: number) => {
    await apiClient.delete(`${CDN_PROVIDERS_BASE}/${id}`);

    // Trigger refresh by updating params
    const currentParams = get(cdnProvidersListParamsAtom);
    set(cdnProvidersListParamsAtom, { ...currentParams, refresh: Date.now() });
  }
);

// Set default CDN provider atom
export const setDefaultCdnProvider = atom(
  null,
  async (get, set, providerId: number) => {
    await apiClient.post(`/cdn-providers/${providerId}/set-default`);
    const currentParams = get(cdnProvidersListParamsAtom);
    set(cdnProvidersListParamsAtom, { ...currentParams, refresh: Date.now() });
  }
);
